import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

type SubjectTribute = {
  id: string;
  full_name: string;
  slug: string;
  taken_down_at: string | null;
  deceased_at: string | null;
  is_published: boolean;
  date_of_birth: string | null;
  date_of_death: string | null;
  residence_region: string | null;
  headline: string | null;
  short_tribute: string | null;
  personality_prompt: string | null;
  raw_recording_public: boolean;
  raw_video_public: boolean;
  tribute_compiled_at: string | null;
  submitted_for_publication_at: string | null;
};

// Map broker deny reason codes to plain, subject-facing text. Never surface raw
// reason codes or verdict strings (go_dark / quiet_delete) to the subject.
const DENY_MESSAGES: Record<string, string> = {
  deceased_executor_route_only:
    "This tribute is managed through the executor process. Please contact support@legacylensai.com.",
  not_authorized: "This tribute is not linked to your account.",
  buyer_cannot_restore: "Only the tribute's subject can restore it.",
};

const friendlyDeny = (reason?: string): string =>
  (reason && DENY_MESSAGES[reason]) ||
  "We could not complete that. Please contact support.";

const Shell = ({ children }: { children: React.ReactNode }) => (
  <div className="min-h-screen bg-background flex items-center justify-center px-4">
    <div className="w-full max-w-md space-y-6">{children}</div>
  </div>
);

const Subject = () => {
  const { toast } = useToast();
  // Tri-state session: undefined = still checking, null = signed out, Session = authed.
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  // Tri-state tribute: undefined = loading, null = none linked, object = the row.
  const [tribute, setTribute] = useState<SubjectTribute | null | undefined>(undefined);
  const [actionLoading, setActionLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  // Post-action status line (set by runEdit); render falls back to the derived
  // load status. Never carries raw flag words ("unpublished"/"revoked").
  const [editStatus, setEditStatus] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({
    full_name: "",
    date_of_birth: "",
    date_of_death: "",
    residence_region: "",
    headline: "",
    short_tribute: "",
    personality_prompt: "",
    raw_recording_public: false,
    raw_video_public: false,
  });

  // Session wiring mirrors ProtectedRoute.tsx: subscribe first, then read once.
  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });
    return () => subscription.unsubscribe();
  }, []);

  const loadTribute = async (userId: string) => {
    const { data, error } = await supabase
      .from("tributes")
      .select("id, full_name, slug, taken_down_at, deceased_at, is_published, date_of_birth, date_of_death, residence_region, headline, short_tribute, personality_prompt, raw_recording_public, raw_video_public, tribute_compiled_at, submitted_for_publication_at")
      .eq("subject_user_id", userId)
      .maybeSingle();
    if (error) {
      toast({
        title: "Could not load your tribute",
        description: error.message,
        variant: "destructive",
      });
      setTribute(null);
      return;
    }
    setTribute((data as SubjectTribute) ?? null);
  };

  useEffect(() => {
    if (session) {
      loadTribute(session.user.id);
    }
  }, [session]);

  // Seed the edit form from the loaded tribute (once compiled). Re-seeds after a
  // save/submit refresh, reflecting the persisted values.
  useEffect(() => {
    if (tribute && tribute.tribute_compiled_at) {
      setEditForm({
        full_name: tribute.full_name ?? "",
        date_of_birth: tribute.date_of_birth ?? "",
        date_of_death: tribute.date_of_death ?? "",
        residence_region: tribute.residence_region ?? "",
        headline: tribute.headline ?? "",
        short_tribute: tribute.short_tribute ?? "",
        personality_prompt: tribute.personality_prompt ?? "",
        raw_recording_public: tribute.raw_recording_public ?? false,
        raw_video_public: tribute.raw_video_public ?? false,
      });
    }
  }, [tribute]);

  const runAction = async (action: "delete" | "restore") => {
    if (!tribute || !session) return;
    setActionLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("tribute-delete-broker", {
        body: { tribute_id: tribute.id, action },
      });
      if (error) throw error;
      const verdict = data?.verdict;
      if (action === "delete") {
        if (verdict === "go_dark" || verdict === "quiet_delete") {
          toast({ title: "Your tribute has been hidden." });
          await loadTribute(session.user.id);
        } else {
          toast({ title: friendlyDeny(data?.reason), variant: "destructive" });
        }
      } else {
        if (verdict === "restore") {
          toast({ title: "Your tribute has been restored." });
          await loadTribute(session.user.id);
        } else {
          toast({ title: friendlyDeny(data?.reason), variant: "destructive" });
        }
      }
    } catch (err) {
      const description =
        err instanceof Error ? err.message : "Please try again in a moment.";
      toast({ title: "Something went wrong", description, variant: "destructive" });
    } finally {
      setActionLoading(false);
    }
  };

  // Plain, non-technical status from the tribute's current state. The post-action
  // message (editStatus) takes precedence over this in render.
  const loadStatusLine = (t: SubjectTribute): string => {
    if (t.is_published) {
      return "Your tribute is live.";
    }
    return "Draft. Publish it when you're ready.";
  };

  const runEdit = async (action: "save" | "submit") => {
    if (!tribute || !session) return;
    setEditLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("subject-edit-tribute", {
        body: {
          tribute_id: tribute.id,
          action,
          fields: {
            full_name: editForm.full_name,
            date_of_birth: editForm.date_of_birth,
            date_of_death: editForm.date_of_death,
            residence_region: editForm.residence_region,
            headline: editForm.headline,
            short_tribute: editForm.short_tribute,
            personality_prompt: editForm.personality_prompt,
            raw_recording_public: editForm.raw_recording_public,
            raw_video_public: editForm.raw_video_public,
          },
        },
      });
      if (error) throw error;
      let msg: string;
      if (action === "save") {
        msg = "Saved.";
      } else {
        msg = "Published. Your tribute is now live.";
      }
      await loadTribute(session.user.id);
      setEditStatus(msg);
    } catch (err) {
      const description =
        err instanceof Error ? err.message : "Please try again in a moment.";
      toast({ title: "Could not save your changes", description, variant: "destructive" });
    } finally {
      setEditLoading(false);
    }
  };

  if (session === undefined) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center text-foreground">
        Loading...
      </div>
    );
  }

  if (session === null) {
    return (
      <Shell>
        <div className="text-center space-y-2">
          <h1 className="font-display text-2xl font-bold text-foreground">
            Sign in to manage your tribute
          </h1>
        </div>
        <Button asChild className="w-full">
          <Link to="/subject-login">Sign in</Link>
        </Button>
      </Shell>
    );
  }

  if (tribute === undefined) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center text-foreground">
        Loading...
      </div>
    );
  }

  if (tribute === null) {
    return (
      <Shell>
        <p className="text-center text-muted-foreground">
          No tribute is linked to your account yet.
        </p>
      </Shell>
    );
  }

  // Deceased gate first: no self-service controls; route to the executor process.
  if (tribute.deceased_at !== null) {
    return (
      <Shell>
        <div className="text-center space-y-2">
          <h1 className="font-display text-2xl font-bold text-foreground">
            {tribute.full_name}
          </h1>
          <p className="text-muted-foreground text-sm">
            Changes to this tribute must go through the executor process. Please
            contact support@legacylensai.com.
          </p>
        </div>
      </Shell>
    );
  }

  // Hidden (taken down, not deceased): offer restore.
  if (tribute.taken_down_at !== null) {
    return (
      <Shell>
        <Alert>
          <AlertTitle>This tribute is hidden</AlertTitle>
          <AlertDescription>You can restore it anytime.</AlertDescription>
        </Alert>
        <Button
          className="w-full"
          disabled={actionLoading}
          onClick={() => runAction("restore")}
        >
          {actionLoading ? "Restoring..." : "Restore tribute"}
        </Button>
      </Shell>
    );
  }

  // Live (both null). Take-down is always available; the edit form appears only
  // once the tribute is compiled. Uncompiled -> a calm "being prepared" note.
  const compiled = tribute.tribute_compiled_at !== null;
  const statusLine = editStatus ?? loadStatusLine(tribute);
  return (
    <Shell>
      <div className="text-center space-y-2">
        <h1 className="font-display text-2xl font-bold text-foreground">
          {tribute.full_name}
        </h1>
      </div>

      {!compiled && (
        <p className="text-center text-muted-foreground text-sm">
          Your tribute is being prepared. You'll be able to review and edit it here soon.
        </p>
      )}

      {compiled && (
        <div className="space-y-6">
          <p className="text-center text-sm text-muted-foreground">{statusLine}</p>

          <div className="space-y-4">
            <div>
              <Label htmlFor="full_name">Full name</Label>
              <Input
                id="full_name"
                value={editForm.full_name}
                onChange={(e) => setEditForm((f) => ({ ...f, full_name: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="date_of_birth">Date of birth</Label>
                <Input
                  id="date_of_birth"
                  type="date"
                  value={editForm.date_of_birth}
                  onChange={(e) => setEditForm((f) => ({ ...f, date_of_birth: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="date_of_death">Date of death</Label>
                <Input
                  id="date_of_death"
                  type="date"
                  value={editForm.date_of_death}
                  onChange={(e) => setEditForm((f) => ({ ...f, date_of_death: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="residence_region">State / region of residence</Label>
              <Input
                id="residence_region"
                value={editForm.residence_region}
                onChange={(e) => setEditForm((f) => ({ ...f, residence_region: e.target.value }))}
                placeholder="New York, USA"
              />
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="headline">Headline</Label>
              <Input
                id="headline"
                value={editForm.headline}
                onChange={(e) => setEditForm((f) => ({ ...f, headline: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="short_tribute">Short tribute</Label>
              <Textarea
                id="short_tribute"
                rows={4}
                value={editForm.short_tribute}
                onChange={(e) => setEditForm((f) => ({ ...f, short_tribute: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="personality_prompt">Personality and voice</Label>
              <Textarea
                id="personality_prompt"
                rows={6}
                value={editForm.personality_prompt}
                onChange={(e) => setEditForm((f) => ({ ...f, personality_prompt: e.target.value }))}
              />
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="font-medium text-sm text-foreground">What visitors can replay</h3>
            <div className="flex items-start gap-3">
              <Switch
                id="raw_recording_public"
                checked={editForm.raw_recording_public}
                onCheckedChange={(v) => setEditForm((f) => ({ ...f, raw_recording_public: v }))}
              />
              <div>
                <Label htmlFor="raw_recording_public">
                  Allow visitors to play my original audio recordings
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Off by default. Lets visitors hear your real recorded voice on your tribute page — separate from the AI voice.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Switch
                id="raw_video_public"
                checked={editForm.raw_video_public}
                onCheckedChange={(v) => setEditForm((f) => ({ ...f, raw_video_public: v }))}
              />
              <div>
                <Label htmlFor="raw_video_public">
                  Allow visitors to play my original video recordings
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Off by default, and separate from the audio setting above. Lets visitors watch your real recorded video — your face and voice — on your tribute page.
                </p>
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              className="w-full"
              disabled={editLoading}
              onClick={() => runEdit("save")}
            >
              {editLoading ? "Saving..." : "Save draft"}
            </Button>
            <Button
              className="w-full"
              disabled={editLoading}
              onClick={() => runEdit("submit")}
            >
              {editLoading ? "Publishing..." : "Publish"}
            </Button>
          </div>
        </div>
      )}

      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button variant="destructive" className="w-full" disabled={actionLoading}>
            {actionLoading ? "Taking down..." : "Take down my tribute"}
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Take down your tribute?</AlertDialogTitle>
            <AlertDialogDescription>
              This hides your tribute from the public. Your recordings and data are
              kept, and you can restore it anytime. Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => runAction("delete")}>
              Take it down
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Shell>
  );
};

export default Subject;
